import { PrescribedBurnEntity } from './../../domain/entities/prescribed-burn-entity';
import { ApiFetchError, BaseError, UnimplementedError } from '@/common/errors';
import {
  BaseHazardEntity,
  CentreEntity,
  CentreSubType,
  ClosureEntity,
  ClosureSubType,
  EnrichedWarningEntity,
  EntitySubType,
  EventEntity,
  FdrSubType,
  TotalFireBanEntity,
  GetCentresModel,
  GetClosuresModel,
  GetEventsModel,
  GetHazardsModel,
  GetIncidentsModel,
  GetS3HazardsModel,
  GetWarningByIdModel,
  GetWarningDetailModel,
  GetWarningIndexModel,
  HazardGeometryState,
  GetWarningsModel,
  HAZARD_MARKERS,
  HazardEntity,
  HazardType,
  HazardsDataSource,
  HazardsRepository,
  IncidentEntity,
  IncidentSubType,
  S3HazardsDataSource,
  WarningEntity,
  WarningSubType,
  centresToSchemeMap,
  closuresToSchemeMap,
  incidentsToSchemeMap,
  localAreaGovernments,
  warningsToSchemeMap,
  HazardPriorityMapping,
  IconName,
  WarningSubTypes,
  FireDangerRatingEntity,
  FireDangerRatingEntityForecast,
} from '@/features/hazards';

import { Either, left, right } from 'fp-ts/lib/Either';
import { Feature, FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { DateTime } from 'luxon';
import { GetBaseHazardModel, GetFireDangerRatingsModelV2, GetTotalFireBansModel, fireDangerWarningsToSchemeMap } from '../models';

import { composeFireBanLga } from '@/common/helpers/compose-fire-ban-lga';
import { captureException } from '@sentry/vue';
import { lgasNameMapping } from '../../presentation/regionToLgaMappings';

/**
 * How many warning detail requests may be in flight at once (KUDO1-9541).
 *
 * Matches the mobile client's `kWarningDetailConcurrency`. Bounding the request count also bounds
 * in-flight bytes at four times the largest geometry, which is why no separate byte budget is
 * needed. It also stays under the six-connection per-origin cap if the distribution ever serves
 * HTTP/1.1, leaving headroom for the index poll and the other hazard types fetching alongside.
 */
export const WARNING_DETAIL_CONCURRENCY = 4;

/**
 * Total attempts per detail request: the first, plus one retry.
 *
 * This is the ONLY retry layer, and it can afford to be. `AxiosSingleton.ewa` is a bare
 * `axios.create({ baseURL })` with no interceptors and the repo has no retry package, so unlike the
 * mobile client there is no transport-level retry underneath that would multiply this.
 */
export const WARNING_DETAIL_ATTEMPTS = 2;

/**
 * Upper bound on the pause before a retry, drawn uniformly from `0..750` ms.
 *
 * Jittered rather than fixed on purpose. Tabs are not all on one cadence — see
 * `HAZARDS_POLL_INTERVAL_MS` and its modal-open counterpart — but a shared upstream failure still
 * lands on many of them at once, and a fixed delay would march every affected tab's retry back into
 * step. The jitter is what spreads them.
 *
 * The bound itself is a chosen small spread, not a derived figure: long enough to decorrelate,
 * short enough to stay invisible next to the request timeout it sits between. Nothing computes
 * against it, so it can be moved on judgement.
 */
export const WARNING_DETAIL_RETRY_DELAY_MS = 750;

/** The geometry the detail resource carries, split as `mapModelToEntity` splits it. */
type MergedGeometry = { polylineFeatures: Feature[]; polygonFeatures: Feature[] };

/** A cached merge result, held against the version it was fetched at. */
type CachedGeometry = MergedGeometry & { version: string };

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Run `worker` over `items` with at most `limit` in flight.
 *
 * A fixed pool of workers pulling from a shared cursor, NOT chunked batches. With batches a single
 * slow record idles the rest of its batch until it settles; with a shared cursor a worker that
 * finishes early immediately takes the next item.
 */
const forEachBounded = async <T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> => {
  let cursor = 0;

  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      await worker(items[cursor++]);
    }
  });

  await Promise.all(runners);
};

export class HazardsRepositoryImpl implements HazardsRepository {
  dataSource: HazardsDataSource;
  s3DataSource: S3HazardsDataSource;

  /**
   * The newest successfully-merged geometry per warning id.
   *
   * The whole geometry cache: in-memory, no persistence, and no eviction policy beyond dropping ids
   * that have left the index. It exists so a failed detail fetch degrades to the last boundary we
   * held rather than rendering the hazard as boundary-free.
   *
   * `HazardsRepositoryImpl` is constructed once in the store setup, so this outlives every poll.
   */
  private readonly geometryCache = new Map<string, CachedGeometry>();

  /**
   * Detail requests in flight, keyed `{id}:{version}`.
   *
   * The results cache holds only *completed* fetches, so without this two overlapping poll cycles
   * both miss and both dispatch. Overlapping cycles are reachable today: the poller runs alongside
   * the manual refresh and the map's mode-change fetch.
   */
  private readonly inFlightDetail = new Map<string, Promise<MergedGeometry>>();

  /** The index request in flight, so two overlapping polls issue one request. */
  private inFlightIndex: Promise<Either<BaseError, WarningEntity[]>> | null = null;

  /**
   * The merge in flight, held against the exact list it was started for.
   *
   * Two side-by-side merges would each compute their own set of merged ids, and the slower one's
   * eviction would drop geometry the faster one had just written. Only one merge being in flight is
   * also what makes a single post-merge eviction safe.
   *
   * The `warnings` reference is what makes the join safe: joining on "a merge is running" alone
   * hands a newer poll the previous poll's merged array, reverting the store to an older warning
   * set. A caller only joins a merge started for its own list; anyone else chains after it.
   */
  private inFlightMerge: { warnings: WarningEntity[]; promise: Promise<WarningEntity[]> } | null = null;

  constructor(dataSource: HazardsDataSource, s3DataSource: S3HazardsDataSource) {
    this.dataSource = dataSource;
    this.s3DataSource = s3DataSource;
  }

  /**
   * @deprecated - The getEvents method should be used instead to get the event details.
   * @param hazardType
   * @param hazardId
   * @returns
   */
  async getHazardById(hazardType: HazardType, hazardId: string): Promise<Either<BaseError, EnrichedWarningEntity>> {
    try {
      let hazardModel: GetWarningByIdModel | undefined;

      switch (hazardType) {
        case HazardType.Warning:
          hazardModel = await this.dataSource.getWarningById(hazardId);
          break;
        default:
          throw new UnimplementedError(`get ${hazardType} by id`);
      }

      return right(hazardModel);
    } catch (e) {
      captureException(e);

      if (e instanceof BaseError) {
        return left(e);
      }

      return left(new ApiFetchError(`${hazardType} - ${hazardId}`));
    }
  }

  async getHazards(hazardType: HazardType): Promise<Either<BaseError, HazardEntity[]>> {
    try {
      let hazardModels: GetHazardsModel[] = [];
      let fireDangerRatingsModels: GetFireDangerRatingsModelV2[] = [];
      let s3HazardModel: GetS3HazardsModel | null = null;

      switch (hazardType) {
        case HazardType.Incident:
          hazardModels = await this.dataSource.getIncidents();
          break;
        case HazardType.Warning:
          hazardModels = await this.dataSource.getWarnings();
          break;
        case HazardType.Centre:
          hazardModels = await this.dataSource.getCentres();
          break;
        case HazardType.Closure:
          hazardModels = await this.dataSource.getOutagesAndClosures();
          break;
        case HazardType.Event:
          hazardModels = await this.dataSource.getEvents();
          break;
        case HazardType.TotalFireBans:
          hazardModels = await this.dataSource.getTotalFireBans();
          break;
        case HazardType.FireDangerRating:
          fireDangerRatingsModels = await this.dataSource.getFireDangerRatings();
          break;
        case HazardType.PrescribedBurns:
          s3HazardModel = await this.s3DataSource.getPrescribedBurns();
          break;
        default:
          throw new UnimplementedError(`get ${hazardType}`);
      }

      if (s3HazardModel) return right(this.sortHazardEntities(this.mapS3ModelsToEntities(hazardType, s3HazardModel)));

      if (hazardType === HazardType.FireDangerRating) {
        return right(this.mapFdrV2ModelsToEntities(fireDangerRatingsModels));
      }

      return right(this.mapModelsToEntities({ hazardModels, hazardType }));
    } catch (e) {
      captureException(e);
      return left(new ApiFetchError(hazardType));
    }
  }

  /**
   * Phase one of the two-phase warnings fetch: the index (KUDO1-9541).
   *
   * Structured exactly like `getHazards`'s warning case — same mapping, same `Either` shape, same
   * `ApiFetchError` on failure — so a caller that already handles that handles this.
   *
   * Two overlapping polls join one request rather than issuing two.
   */
  async getWarningsIndex(): Promise<Either<BaseError, WarningEntity[]>> {
    if (this.inFlightIndex) return this.inFlightIndex;

    this.inFlightIndex = this.fetchWarningsIndex().finally(() => {
      this.inFlightIndex = null;
    });

    return this.inFlightIndex;
  }

  private async fetchWarningsIndex(): Promise<Either<BaseError, WarningEntity[]>> {
    try {
      const hazardModels = await this.dataSource.getWarningsIndex();

      return right(
        this.mapModelsToEntities({ hazardModels: hazardModels as GetHazardsModel[], hazardType: HazardType.Warning }) as WarningEntity[]
      );
    } catch (e) {
      captureException(e);
      // Deliberately does not touch the geometry cache. An index failure says nothing about the
      // geometry we already hold, and discarding it would turn one bad poll into a screen with no
      // boundaries on it.
      return left(new ApiFetchError(HazardType.Warning));
    }
  }

  /**
   * Phase two: fetch each record's boundary and merge it onto the entity (KUDO1-9541).
   *
   * Never renders a hazard as boundary-free because a fetch failed. The ladder is fresh geometry,
   * then the newest cached geometry at any version flagged `stale`, then `unavailable` — which is
   * distinct from `none`, the state of a hazard that legitimately has no boundary. Without that
   * distinction a hazard with no mapped boundary and a hazard whose boundary failed to load look
   * identical on the map.
   *
   * Returns new objects rather than mutating in place, so Vue's reactivity sees the change.
   *
   * On wall time: a single detail can burn `EWA_REQUEST_TIMEOUT_MS` twice plus the retry jitter
   * before it gives up, so at `WARNING_DETAIL_CONCURRENCY` in flight a full merge's worst case is
   * roughly `ceil(needsFetch / 4)` times that. Past about a dozen records needing a fetch, that
   * exceeds `HAZARDS_POLL_INTERVAL_MS` and the next poll's merge chains behind this one rather than
   * replacing it. Correctness holds — the join below is what serialises them — but boundaries on
   * screen fall further behind the index the longer the queue gets, during exactly the busy events
   * where the gap matters most. Raising the request timeout lowers that threshold.
   *
   * A response that settles after its record has left the index is still merged and cached; the
   * post-merge eviction loop is the SOLE mechanism that retires it, and it runs against the index
   * this merge was given. There is deliberately no mid-flight "did this record leave" check: the
   * only id set such a check could consult is the one captured before dispatch, so it could never
   * be false, and the join guard above already keeps one merge in flight at a time.
   */
  async mergeWarningGeometry(warnings: WarningEntity[]): Promise<WarningEntity[]> {
    const current = this.inFlightMerge;

    // Same list, same answer. This is the overlapping-poll case the join exists for.
    if (current?.warnings === warnings) return current.promise;

    // A different list chains behind the running merge rather than racing it, so exactly one merge
    // is ever in flight and the post-merge eviction loop stays safe. The predecessor's rejection is
    // swallowed here only to release the chain; its own caller still sees it.
    const promise = (current ? current.promise.catch(() => {}) : Promise.resolve())
      .then(() => this.runWarningGeometryMerge(warnings))
      .finally(() => {
        // Only clear the slot if it is still ours — a later caller may already have replaced it.
        if (this.inFlightMerge?.promise === promise) this.inFlightMerge = null;
      });

    this.inFlightMerge = { warnings, promise };

    return promise;
  }

  /**
   * Everything the in-memory cache can answer about this index, without touching the network
   * (KUDO1-9541, mobile parity with `_mergeGeometry`'s `onCacheResolved` frame).
   *
   * Synchronous on purpose: the caller assigns this INSTEAD of the raw index, so the first frame of
   * a poll already carries every boundary the client holds. Assigning the raw index hands Mapbox a
   * warning set whose polygon arrays are empty — the index `geo-source` is Point-only — so every
   * boundary blinks off and back on once per poll.
   *
   * `needsFetch` is false when nothing is left to do, which lets the caller skip the merge and its
   * second assignment entirely rather than pushing an identical frame.
   *
   * Three invariants, all carried over from the mobile client:
   *  - a same-version cache hit goes out `fresh` on this first frame, not on the merge's;
   *  - a record still awaiting a fetch carries its prior geometry forward as `stale`, never
   *    `fresh` — `warningsWithDegradedGeometry` reads this field, and `fresh` would assert the
   *    boundary matches a version it does not;
   *  - a record whose new index entry no longer advertises geometry carries NOTHING forward. A
   *    polygon the issuing authority has withdrawn is the worse artefact on an emergency map.
   */
  resolveCachedWarningGeometry(warnings: WarningEntity[]): { warnings: WarningEntity[]; needsFetch: boolean } {
    const { resolved, needsFetch } = this.partitionWarningGeometry(warnings);

    // The merge normally owns eviction, and does it after its fetches settle. When nothing needs
    // fetching the merge never runs, so ids that left the index would keep their geometry for the
    // lifetime of the tab. Gated on no merge being in flight: evicting alongside one races the
    // fetches it is still awaiting, which is the reason eviction is post-merge in the first place.
    if (needsFetch.length === 0 && !this.inFlightMerge) this.evictDepartedGeometry(new Set(warnings.map((w) => w.id)));

    return { warnings: resolved, needsFetch: needsFetch.length > 0 };
  }

  /**
   * Split an index into what the cache already answers and what still needs a detail fetch.
   *
   * The single place that decides "is this record's boundary already in hand", so the first-frame
   * assignment and the merge cannot drift apart on that question.
   */
  private partitionWarningGeometry(warnings: WarningEntity[]): { resolved: WarningEntity[]; needsFetch: WarningEntity[] } {
    const needsFetch: WarningEntity[] = [];

    const resolved = warnings.map((warning) => {
      const detail = warning.detail;

      if (!detail?.contains?.includes('geometry')) {
        // The server no longer says this record has a boundary, so neither do we. `none`, never
        // `unavailable`: this hazard has no boundary rather than a boundary we failed to load — and
        // nothing is carried forward, which is what retracts a withdrawn polygon.
        return { ...warning, polylineFeatures: [], polygonFeatures: [], geometryState: 'none' as const };
      }

      const cached = this.geometryCache.get(warning.id);

      // Already holding this exact revision. The URL is immutable and version-bearing, so a cache
      // hit at the same version is as fresh as a refetch would be — including on this first frame.
      if (cached?.version === detail.version) return this.withGeometry(warning, cached, 'fresh');

      needsFetch.push(warning);

      // Carried forward while its fetch is outstanding. Same trade the `stale` rung already makes:
      // a one-poll-old boundary beats no boundary. A record with nothing cached is left as the
      // index sent it, which is correct only because it had no boundary on screen to lose.
      return cached ? this.withGeometry(warning, cached, 'stale') : warning;
    });

    return { resolved, needsFetch };
  }

  private async runWarningGeometryMerge(warnings: WarningEntity[]): Promise<WarningEntity[]> {
    // The cache-answerable half is already decided, so the merge neither re-reads those entries nor
    // dispatches for them. What is left is exactly the records a fetch can change.
    const { resolved, needsFetch } = this.partitionWarningGeometry(warnings);

    const outcomes = new Map<string, { geometry: MergedGeometry; state: HazardGeometryState }>();

    const candidates = [...needsFetch]
      // Priority descending, so the most urgent hazard's boundary lands first. Deliberately NOT
      // ordered by geometry size: on an emergency feed the largest boundary is often the least
      // urgent one, and serving small-first would leave an emergency warning's boundary last.
      .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));

    await forEachBounded(candidates, WARNING_DETAIL_CONCURRENCY, async (warning) => {
      // Each record's fetch-and-merge is contained. Without this an unexpected throw unwinds
      // through the worker pool and empties the whole hazard list while sibling workers are still
      // writing — a failure the mobile client hit before adding the same guard.
      try {
        outcomes.set(warning.id, await this.resolveWarningGeometry(warning));
      } catch (e) {
        captureException(e);
        outcomes.set(warning.id, this.fallbackWarningGeometry(warning.id));
      }
    });

    const merged = resolved.map((warning) => {
      const outcome = outcomes.get(warning.id);

      // No fetch was dispatched for this record, so the cache-resolved entry already above is the
      // final answer: a `fresh` hit, or a `none` retraction.
      if (!outcome) return warning;

      return {
        ...warning,
        // Stamped per merge rather than baked into the cache entry: `priority` is a dense position
        // over the whole sorted collection and changes whenever any warning is published or
        // expires, so a cached stamp would drift polygon z-order away from marker order.
        polylineFeatures: this.stampPriority(outcome.geometry.polylineFeatures, warning.priority),
        polygonFeatures: this.stampPriority(outcome.geometry.polygonFeatures, warning.priority),
        geometryState: outcome.state,
      };
    });

    // After the merge resolves, never during it. Evicting mid-merge races the fetches the merge is
    // still awaiting; the whole-poll join guard is what makes one post-merge eviction safe.
    this.evictDepartedGeometry(new Set(warnings.map((warning) => warning.id)));

    return merged;
  }

  /**
   * Drop cached geometry for ids this index no longer carries.
   *
   * The only mechanism bounding the cache. A record that has left the index will never be rendered
   * again, and keeping its entry is actively harmful: it would survive as "last-known geometry" and
   * could later be offered as a stale fallback for a record that has since moved to a new version.
   */
  private evictDepartedGeometry(indexIds: Set<string>): void {
    for (const id of this.geometryCache.keys()) {
      if (!indexIds.has(id)) this.geometryCache.delete(id);
    }
  }

  /** One record, wearing the geometry it was given, priority stamped for this poll's ordering. */
  private withGeometry(warning: WarningEntity, geometry: MergedGeometry, state: HazardGeometryState): WarningEntity {
    return {
      ...warning,
      polylineFeatures: this.stampPriority(geometry.polylineFeatures, warning.priority),
      polygonFeatures: this.stampPriority(geometry.polygonFeatures, warning.priority),
      geometryState: state,
    };
  }

  /**
   * Fetch, validate and cache one record's geometry, or fall back.
   *
   * Only ever called for a record `partitionWarningGeometry` put in `needsFetch`, so the
   * same-version cache check lives there and is deliberately not repeated here.
   */
  private async resolveWarningGeometry(warning: WarningEntity): Promise<{ geometry: MergedGeometry; state: HazardGeometryState }> {
    const { url, version } = warning.detail!;

    const key = `${warning.id}:${version}`;

    try {
      // The stored reference and the awaited reference must be the same decorated promise. Storing
      // the `.finally()` result while awaiting the bare one leaves the stored copy unobserved, and
      // a rejected detail fetch then surfaces as an unhandled rejection.
      let pending = this.inFlightDetail.get(key);

      if (!pending) {
        pending = this.fetchWarningGeometry(url, version).finally(() => this.inFlightDetail.delete(key));
        this.inFlightDetail.set(key, pending);
      }

      const geometry = await pending;

      // Only a confirmed write marks this version merged, so a mid-poll failure leaves the older
      // geometry in place as the fallback.
      this.geometryCache.set(warning.id, { version, ...geometry });

      return { geometry, state: 'fresh' };
    } catch (e) {
      captureException(e);
      return this.fallbackWarningGeometry(warning.id);
    }
  }

  /** The newest cached geometry at any version, or nothing at all. */
  private fallbackWarningGeometry(warningId: string): { geometry: MergedGeometry; state: HazardGeometryState } {
    const cached = this.geometryCache.get(warningId);

    if (cached) {
      return { geometry: { polylineFeatures: cached.polylineFeatures, polygonFeatures: cached.polygonFeatures }, state: 'stale' };
    }

    // Distinct from `none`. This hazard has a boundary and we could not load it, which is a
    // different thing to tell a user than "this hazard has no boundary".
    return { geometry: { polylineFeatures: [], polygonFeatures: [] }, state: 'unavailable' };
  }

  /** One detail request, with a single jittered retry, and the version echo check. */
  private async fetchWarningGeometry(url: string, expectedVersion: string): Promise<MergedGeometry> {
    let lastError: unknown;

    for (let attempt = 1; attempt <= WARNING_DETAIL_ATTEMPTS; attempt++) {
      // One controller per attempt, aborted in `finally`. The instance timeout would eventually
      // settle a stalled request, but an attempt this loop has already given up on — a version echo
      // mismatch, say — would otherwise keep its socket for the rest of that budget while the retry
      // competes for the same four-request pool.
      const controller = new AbortController();

      try {
        const detailModel = await this.dataSource.getWarningDetail(url, controller.signal);

        // Treated as a fetch failure rather than merged. An immutable URL that answers with a
        // different revision is not something to render; invalidation is not atomic, so a stale
        // index meeting a fresh record is reachable.
        if (detailModel.detail?.version !== expectedVersion) {
          throw new Error('Detail response echoed a version other than the one advertised');
        }

        return this.splitDetailGeometry(detailModel);
      } catch (e) {
        lastError = e;
      } finally {
        // A no-op on the success path: the response has already been read by the time this runs.
        // Before the backoff, not after, so a dead attempt is not still holding a socket while the
        // loop sleeps.
        controller.abort();
      }

      if (attempt < WARNING_DETAIL_ATTEMPTS) {
        await sleep(Math.random() * WARNING_DETAIL_RETRY_DELAY_MS);
      }
    }

    throw lastError;
  }

  /**
   * Split the detail record's non-Point geometry, enriching each kind in its OWN pass.
   *
   * Two passes, not one combined array, and it matters: `enrichFeatures` folds the array position
   * into `timeAwareFeatureId`, and `mapModelToEntity` builds two independent 0-based arrays. A
   * single combined pass would shift every polygon's index, change its feature identity, and churn
   * Mapbox features on every merge.
   *
   * Enriched from the detail model rather than the index entity, because `enrichFeature` reads
   * `id`, `published-date-time`, `updated-date-time`, `entityType`, `entitySubType` and `event` off
   * a raw model — keys an entity does not carry in that shape. The detail response carries all six
   * identically to the index record, which is what makes the ids come out the same as the
   * single-endpoint path produces today.
   */
  private splitDetailGeometry(detailModel: GetWarningDetailModel): MergedGeometry {
    const geoSource = detailModel['geo-source'] as FeatureCollection | undefined;
    const features = geoSource?.features ?? [];

    const polylines = features.filter(
      (feature) => feature?.geometry?.type === 'LineString' || feature?.geometry?.type === 'MultiLineString'
    );
    const polygons = features.filter((feature) => feature?.geometry?.type === 'Polygon' || feature?.geometry?.type === 'MultiPolygon');

    return {
      polylineFeatures: this.enrichFeatures(polylines, detailModel as GetHazardsModel),
      polygonFeatures: this.enrichFeatures(polygons, detailModel as GetHazardsModel),
    };
  }

  private stampPriority(features: Feature[], priority?: number): Feature[] {
    return features.map((feature) => ({ ...feature, properties: { ...feature.properties, priority } }));
  }

  // API Models
  mapModelsToEntities({ hazardModels, hazardType }: { hazardModels: GetHazardsModel[]; hazardType: HazardType }): HazardEntity[] {
    switch (hazardType) {
      case HazardType.Warning:
      case HazardType.Incident:
      case HazardType.Centre:
      case HazardType.Closure:
        return hazardModels.map((model) => this.mapModelToEntity(model));
      case HazardType.Event:
        return this.sortHazardEntities(hazardModels.map((model) => this.mapModelToEntity(model)));
      case HazardType.TotalFireBans:
        return this.mapTotalFireBanModelToEntity(hazardModels as GetTotalFireBansModel[]);
      default:
        throw new UnimplementedError(`get ${hazardType}`);
    }
  }

  mapModelToEntity(hazardModel: GetBaseHazardModel): HazardEntity {
    const geoSource = hazardModel?.['geo-source'] as FeatureCollection;
    const geoSourceFeatures = geoSource?.['features'] ?? [];

    const points = geoSourceFeatures.filter((feature) => feature.geometry.type === 'Point');
    const pointFeatures = this.enrichFeatures(points, hazardModel);

    const polylines = geoSourceFeatures.filter(
      (feature) => feature.geometry.type === 'LineString' || feature.geometry.type === 'MultiLineString'
    );
    const polylineFeatures = this.enrichFeatures(polylines, hazardModel);

    const polygons = geoSourceFeatures.filter((feature) => feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon');
    const polygonFeatures = this.enrichFeatures(polygons, hazardModel);

    const baseHazardEntity: BaseHazardEntity = {
      id: hazardModel.id!,
      entityType: hazardModel.entityType as HazardType,
      entitySubType: hazardModel.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType],

      eventId: hazardModel.event!,

      title: hazardModel.name,
      publishedAt: hazardModel['published-date-time'],
      issuedAt: hazardModel['issued-date-time'],

      polygonFeatures,
      polylineFeatures,
      pointFeatures,
      pointFeature: pointFeatures.at(0),
    };

    let partialHazardEntity: Partial<HazardEntity> = {};

    switch (hazardModel.entityType) {
      case HazardType.Warning:
        partialHazardEntity = this.mapWarningModelToPartialEntity(hazardModel as GetWarningsModel);
        break;
      case HazardType.Incident:
        partialHazardEntity = this.mapIncidentModelToPartialEntity(hazardModel as GetIncidentsModel);
        break;
      case HazardType.Centre:
        partialHazardEntity = this.mapCentreModelToPartialEntity(hazardModel as GetCentresModel);
        break;
      case HazardType.Closure:
        partialHazardEntity = this.mapClosureModelToPartialEntity(hazardModel as GetClosuresModel);
        break;
      case HazardType.Event:
        partialHazardEntity = this.mapEventModelToPartialEntity(hazardModel as GetEventsModel);
        break;

      default:
        break;
    }

    const hazardEntity: HazardEntity = { ...baseHazardEntity, ...(partialHazardEntity as HazardEntity) };

    return hazardEntity;
  }

  mapEventModelToPartialEntity(eventModel: GetEventsModel): Partial<EventEntity> {
    const banDate = eventModel['date-of-ban'];
    return {
      publishedAt: eventModel['published-date-time'],
      issuingAgency: eventModel['issuing-agency'],
      eventSummary: eventModel['web-message'],
      eventSummaryText: eventModel['web-text-message'],
      dateOfBan: banDate ? DateTime.fromISO(banDate) : undefined,
      banInformation: eventModel['ban-information'],
      rawEvent: eventModel,
    };
  }

  /**
   * Sort hazard entities by priority and, if needed, the published date.
   */
  sortHazardEntities(hazardEntities: HazardEntity[]): HazardEntity[] {
    return hazardEntities.sort((a, b) => {
      const aPriority = HazardPriorityMapping[a.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType]] ?? 0;
      const bPriority = HazardPriorityMapping[b.entitySubType as (typeof EntitySubType)[keyof typeof EntitySubType]] ?? 0;

      /**
       * This is to ensure warnings of the same type (Bushfire) are sorted by priority.
       * @example
       *  - x Emergency Warning comes before x Watch and Act Warning
       */
      if (Math.floor(aPriority) !== Math.floor(bPriority)) {
        return bPriority - aPriority;
      }

      // Sort by published date if the priority is the same
      const aPublishedAt = a.publishedAt as string;
      const bPublishedAt = b.publishedAt as string;

      if (aPublishedAt && bPublishedAt) {
        const aMillis = DateTime.fromISO(aPublishedAt).toMillis();
        const bMillis = DateTime.fromISO(bPublishedAt).toMillis();

        return bMillis - aMillis;
      }

      /**
       * This is to ensure warnings of different types (Bushfire, Flood, etc) but the same alert level (Emergency, Watch and Act, etc)
       * are sorted by priority.
       * @example
       *  - Bushfire Emergency Warning comes before Flood Emergency Warning
       */
      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }

      // Sort by title (alphabetically) if the published date is the same
      const aTitle = a.title;
      const bTitle = b.title;

      if (aTitle && bTitle) {
        return aTitle.localeCompare(bTitle);
      }

      return 0;
    });
  }

  mapWarningModelToPartialEntity(warningModel: GetWarningsModel): Partial<WarningEntity> {
    // Cast to the app's own sub-type: the generated api-schema union lags behind the
    // template, so newer sub-types (e.g. warnings_biosecurity) are absent from it.
    const entitySubType = warningModel['entitySubType'] as WarningSubType;
    const scheme = warningsToSchemeMap[entitySubType];

    /**
     * Most warning types always use the same icon, taken from `warningsToSchemeMap`.
     * These two are the exception: each record stores its own icon in `warning-icon`.
     *  - Other Warning: the operator picks the icon.
     *  - Biosecurity: the workspace sets it from the record's `biosecurity-type`
     *    (Animal Disease / Plant Pest / Aquatic Pest).
     */
    const hasIconOnRecord =
      entitySubType === WarningSubTypes.OtherWarningWarningEntity || entitySubType === WarningSubTypes.BiosecurityWarningEntity;

    if (hasIconOnRecord && warningModel['warning-icon']) {
      scheme.icon = HAZARD_MARKERS[warningModel['warning-icon'] as IconName] ?? scheme.icon;
    }

    return {
      ...scheme,
      description: this.composeWarningDescription(warningModel),
      warningType: warningModel['warning-type'],
      publishingStatus: warningModel['publishing-status'],
      geoSource: warningModel['geo-source'],
      alertLine: warningModel['alert-line'],
      safeToLeave: warningModel['safe-to-leave'],
      impactToHomesNote: warningModel['impact-to-homes-note'],
      telephoneWarning: warningModel['telephone-warning'],
      alertLevelNote: warningModel['alert-level-note'],
      threatToHomesNote: warningModel['threat-to-homes-note'],
      fireLocationNote: warningModel['fire-location-note'],
      fireDirectionNote: warningModel['fire-direction-note'],
      whatToDoNote: warningModel['what-to-do-note'],
      safestRouteNote: warningModel['safest-route-note'],
      sortingPriority: warningModel['sorting-priority'],
      actionStatement: warningModel['action-statement'],
      // KUDO1-9541. Present on an index record, absent on a `/v1/warnings` one, so both stay
      // `undefined` for the existing callers. Passed straight through: `detail.version` is opaque
      // and must not be parsed as a date or reformatted, and `priority` is the list position
      // rather than `sortingPriority`.
      detail: (warningModel as GetWarningIndexModel).detail,
      priority: (warningModel as GetWarningIndexModel).priority,
    };
  }

  mapIncidentModelToPartialEntity(incidentModel: GetIncidentsModel): Partial<IncidentEntity> {
    const entitySubType = incidentModel['entitySubType'];
    const scheme = incidentsToSchemeMap[entitySubType as IncidentSubType];

    const incidentIcon = incidentModel['incident-icon'] as IconName | undefined;

    if (entitySubType === IncidentSubType.OtherIncidentEntity && incidentIcon) {
      scheme.icon = HAZARD_MARKERS[incidentIcon] ?? scheme?.icon;
    }

    return {
      ...scheme,
      lga: incidentModel['lga'],
      incidentType: incidentModel['incident-type'],
      reportedAt: incidentModel['start-date-time'],
      /**
       * The `updated-date-time` is used for both `publishedAt` and `updatedAt`.
       *
       * Some incidents don't have an `updated-date-time` and only have a `published-date-time`.
       * As such, we fallback to using the `published-date-time` if the `updated-date-time` is not available.
       */
      publishedAt: incidentModel['updated-date-time'] ?? incidentModel['published-date-time'],
      updatedAt: incidentModel['updated-date-time'] ?? incidentModel['published-date-time'],
      incidentStatus: incidentModel['incident-status'],
      description: this.composeIncidentDescription(incidentModel),
      reportedNear: this.composeIncidentReportedNear(incidentModel),
      reportedIn:
        incidentModel.entitySubType !== EntitySubType.EarthquakeIncidentEntity ? incidentModel['suburbs']?.at(0)?.toUpperCase() : undefined,
      earthquakeMagnitude: incidentModel['earthquake-magnitude'],
      earthquakeDepth: incidentModel['earthquake-depth'],
      submitFeltReportNote: incidentModel['submit-felt-report-note'],
    };
  }

  mapClosureModelToPartialEntity(closureModel: GetClosuresModel): Partial<ClosureEntity> {
    const entitySubType = closureModel['entitySubType'];
    const scheme = closuresToSchemeMap[entitySubType as ClosureSubType];

    return {
      ...scheme,
      description: closureModel['headline'] ?? '',
    };
  }

  mapCentreModelToPartialEntity(centreModel: GetClosuresModel): Partial<CentreEntity> {
    const entitySubType = centreModel['entitySubType'];
    const scheme = centresToSchemeMap[entitySubType as CentreSubType];

    return {
      ...scheme,
      description: centreModel['headline'] ?? '',
    };
  }

  mapTotalFireBanModelToEntity(totalFireBanModel: GetTotalFireBansModel[]): HazardEntity[] {
    const activeTotalFireBans = totalFireBanModel.map((model) => {
      const banDate = model['date-of-ban'];
      const expiry = model['expiry'];

      const banLevel = model['ban-level'];
      const isActive = banLevel === 'Ban';
      const rawLgaName = model['fire-ban-lga'];
      const sanitisedLga = composeFireBanLga(rawLgaName);

      const scheme =
        warningsToSchemeMap[isActive ? WarningSubTypes.TotalFireBanWarningEntity : WarningSubTypes.NoTotalFireBanWarningEntity];

      let description;

      switch (banLevel) {
        case 'Ban':
          description = 'Total Fire Ban';
          break;
        case 'No ban':
          description = 'No Total Fire Ban';
          break;
        case 'No ban - revoked':
          description = 'Total Fire Ban - Revoked';
          break;
        default:
          description = 'Total Fire Ban';
          break;
      }

      return {
        ...scheme,
        // We are using the rawLgaName as the id this enables us to
        // efficiently filter the map layers (given we're using tile ets)
        id: rawLgaName,
        banLevel,
        description,
        isActive,
        eventId: model.event!,
        title: sanitisedLga,
        rawLgaName,
        banDuration: model['ban-duration'],
        lga: sanitisedLga,
        entitySubType: model['entitySubType'] as WarningSubType,
        publishedAt: model['published-date-time'],
        entityType: HazardType.TotalFireBans,
        dateOfBan: banDate ? DateTime.fromISO(banDate) : undefined,
        expiry: expiry ? DateTime.fromISO(expiry) : undefined,
      } as TotalFireBanEntity;
    });

    // This will be used to enrich the TFB items with the inactive fire bans
    const inActiveTotalFireBans = localAreaGovernments.map((lga) => {
      const sanitisedLga = composeFireBanLga(lga);
      return {
        ...warningsToSchemeMap[WarningSubTypes.NoTotalFireBanWarningEntity],
        id: lga,
        title: sanitisedLga,
        eventId: '',
        description: 'No Total Fire Ban',
        banLevel: 'No ban',
        banDuration: 'All day',
        isActive: false,
        rawLgaName: lga,
        lga: sanitisedLga,
        polygonFeatures: [],
        entitySubType: WarningSubTypes.TotalFireBanWarningEntity,
        publishedAt: totalFireBanModel?.at(0)?.['published-date-time'],
        entityType: HazardType.TotalFireBans,
      } as TotalFireBanEntity;
    });

    // Group the fire ban by ban level, then sort each group alphabetically
    const activeFireBanItems = activeTotalFireBans.filter((item) => item.isActive).sort((a, b) => a.lga.localeCompare(b.lga));
    const inactiveRemoteFireBanItems = activeTotalFireBans.filter((item) => !item.isActive);
    const inactiveFireBanItems = inactiveRemoteFireBanItems
      .concat(inActiveTotalFireBans)
      .filter((item) => !item.isActive)
      .sort((a, b) => a.lga.localeCompare(b.lga));

    return activeFireBanItems.concat(inactiveFireBanItems);
  }

  // S3 Models
  mapS3ModelsToEntities(hazardType: HazardType, hazardModel: GetS3HazardsModel): HazardEntity[] {
    switch (hazardType) {
      case HazardType.PrescribedBurns:
        return hazardModel.features.map((model) => this.mapPbModelToEntity(model));
      default:
        throw new UnimplementedError(`get ${hazardType}`);
    }
  }

  // Fire Danger Ratings Model (V2)
  mapFdrV2ModelsToEntities(models: GetFireDangerRatingsModelV2[]): HazardEntity[] {
    return models.map((model) => this.mapFdrV2ModelToEntity(model));
  }

  mapFdrV2ModelToEntity(model: GetFireDangerRatingsModelV2): HazardEntity {
    const forecasts: FireDangerRatingEntityForecast[] = [];

    for (const [, forecast] of Object.entries(model.forecasts ?? {})) {
      let fdrSubType: FdrSubType;
      switch (forecast.fireDanger) {
        case 'Catastrophic':
          fdrSubType = FdrSubType.CatastrophicFdrEntity;
          break;
        case 'Extreme':
          fdrSubType = FdrSubType.ExtremeFdrEntity;
          break;
        case 'High':
          fdrSubType = FdrSubType.HighFdrEntity;
          break;
        case 'Moderate':
          fdrSubType = FdrSubType.ModerateFdrEntity;
          break;
        case 'No Rating':
        default:
          fdrSubType = FdrSubType.NoRatingFdrEntity;
          break;
      }

      forecasts.push({
        fdrSubType: fdrSubType,
        fireDanger: forecast.fireDanger ?? '',
        behaviourIndex: forecast.behaviourIndex ?? '',
        scheme: fireDangerWarningsToSchemeMap[fdrSubType],
      });
    }

    const baseHazardEntity: FireDangerRatingEntity = {
      id: model.area ?? '',
      entityType: HazardType.FireDangerRating,
      title: model.description ?? '',
      issuedAt: this.safeParseIsoDate(model.issueDateTime),
      forecasts,
    };

    return baseHazardEntity;
  }

  /**
   * Safely parses an ISO date string and returns a valid ISO string or undefined.
   * @param dateString The ISO date string to parse.
   * @returns A valid ISO date string or undefined if the input is invalid.
   * @example
   * safeParseIsoDate('2023-10-01T12:00:00Z') => '2023-10-01T12:00:00.000Z'
   * safeParseIsoDate('invalid-date') => undefined
   * safeParseIsoDate(undefined) => undefined
   */
  private safeParseIsoDate(dateString: string | undefined): string | undefined {
    if (!dateString) return undefined;

    const parsedDate = DateTime.fromISO(dateString);
    return parsedDate.isValid ? parsedDate.toISO() : undefined;
  }

  mapPbModelToEntity(feature: Feature<Geometry, GeoJsonProperties>): HazardEntity {
    const burnCenterCoordinates = [
      (feature.properties?.['burn_target_long'] as number | undefined) ?? 0,
      (feature.properties?.['burn_target_lat'] as number | undefined) ?? 0,
    ];

    const pointFeature = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: burnCenterCoordinates,
      },
      properties: {
        id: feature.properties?.['burn_id'],
        featureId: feature.id,
        entityType: HazardType.PrescribedBurns,
        'icon-name': 'ew-prescribed-burn-or-burn-off',
      },
    } as Feature<Geometry, GeoJsonProperties>;

    const baseHazardEntity: PrescribedBurnEntity = {
      id: feature.properties?.['burn_id'],
      entityType: HazardType.PrescribedBurns,
      title: 'Prescribed Burn',
      description: feature.properties?.['location'],
      icon: HAZARD_MARKERS['ew-prescribed-burn-or-burn-off'],

      burnTargetDate: feature.properties?.['burn_target_date'],
      publishedAt: DateTime.fromJSDate(new Date(feature.properties?.['burn_target_date_raw'])).toISO() ?? undefined,
      burnEstimatedStart: feature.properties?.['burn_est_start'],
      burnStatus: feature.properties?.['burn_stat'],
      burnLocation: feature.properties?.['location'],
      burnForestBlocks: feature.properties?.['forest_blocks'],
      burnArea: feature.properties?.['indicative_area'],
      burnPlannedAreaToday: feature.properties?.['burn_planned_area_today'],
      burnPlannedDistanceToday: feature.properties?.['burn_planned_distance_today'],
      burnCoordinates: [
        (feature.properties?.['burn_target_long'] as number | undefined) ?? 0,
        (feature.properties?.['burn_target_lat'] as number | undefined) ?? 0,
      ],
      burnPurpose: feature.properties?.['burn_purpose'],

      pointFeature: pointFeature,
      pointFeatures: [pointFeature],
      polygonFeatures: [feature],
    };

    return baseHazardEntity;
  }

  enrichFeatures(features: Feature<Geometry, GeoJsonProperties>[], hazardModel: GetHazardsModel) {
    return features.map((feature, index) => this.enrichFeature(feature, hazardModel, index));
  }

  enrichFeature(
    feature: Feature<Geometry, GeoJsonProperties>,
    hazardModel: GetHazardsModel,
    index?: number
  ): Feature<Geometry, GeoJsonProperties> {
    const updatedAt = DateTime.fromISO(hazardModel['published-date-time'] ?? hazardModel['updated-date-time'] ?? '');

    return {
      ...feature,
      // TODO: Type the properties object
      properties: {
        ...feature.properties,
        id: hazardModel.id,
        // The `timeAwareFeatureId` is used to uniquely identify the feature on the map.
        // It is a combination of the hazard id and the updated date-time.
        // This is to ensure that the feature is updated on the map when the hazard id remains the same
        // but the hazard has been updated.
        // We add an index, if given, to allow for multiple features within a
        // single event to be differentiated between. Without, this was causing
        // warnings with multiple markers to only display a single marker.
        timeAwareFeatureId: `${hazardModel.id}-${updatedAt?.toMillis()}${index !== undefined && '-' + index.toString()}`,
        featureId: feature.id,
        entityType: hazardModel.entityType,
        entitySubType: hazardModel.entitySubType,
        eventId: hazardModel?.event,
        updatedAt: hazardModel['published-date-time'],
        ...this.composeFireShapeDetails(hazardModel),
      },
    };
  }

  /**
   * Note that this assumes that only one polygon feature is present in the geoSourceFeatures. (Business decision)
   */
  composeFireShapeDetails(hazardModel: GetHazardsModel) {
    const geoSource = hazardModel?.['geo-source'] as FeatureCollection;
    const geoSourceFeatures = geoSource?.['features'] ?? [];

    const polygonFeature = geoSourceFeatures
      .filter((feature) => feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon')
      ?.at(0);

    if (
      !polygonFeature ||
      (hazardModel.entityType as HazardType) !== HazardType.Incident ||
      (hazardModel.entitySubType as IncidentSubType) === IncidentSubType.EarthquakeIncidentEntity ||
      (!polygonFeature.properties?.['date-time'] && !hazardModel['published-date-time'])
    )
      return {};

    const fireShapeUpdatedAt = DateTime.fromISO(polygonFeature.properties?.['date-time'] ?? hazardModel['published-date-time']).toFormat(
      'dd/MM/yyyy HH:mm a'
    );

    return {
      fireShapeLabel: `Incident Area as at: ${fireShapeUpdatedAt}`,
    };
  }

  composeIncidentReportedNear(incidentModel: GetIncidentsModel): string {
    const streetName = incidentModel['location']?.value;
    const nearestCrossStreet = incidentModel['nearest-cross-street']?.value;
    const suburb = incidentModel['suburbs']?.at(0)?.toUpperCase();
    const isEarthquake = incidentModel.entitySubType === EntitySubType.EarthquakeIncidentEntity;

    let content = '';

    if (streetName) {
      content = `${streetName.toUpperCase()}`;
    }

    if (isEarthquake) return content;

    if (nearestCrossStreet) {
      content += streetName?.length ? ' and ' : '';
      content += `${nearestCrossStreet.toUpperCase()}`;
    }

    if (suburb && content?.length) {
      content += `, in ${suburb}`;
    }

    return content;
  }

  composeIncidentDescription(incidentModel: GetIncidentsModel): string {
    const locationValue = incidentModel.location?.value ?? '';
    if (Boolean(locationValue) && incidentModel.entitySubType === EntitySubType.EarthquakeIncidentEntity) {
      return locationValue.toUpperCase();
    }

    const suburb = incidentModel['suburbs']?.at(0)?.toUpperCase() ?? '';
    const lgas = incidentModel['lga']?.map((lga) => lgasNameMapping.get(lga)?.toUpperCase() ?? lga.toUpperCase()).join(', ') ?? '';

    let content = '';

    if (suburb) {
      content = `${suburb}`;
    }

    if (suburb && lgas) {
      content += ', ';
    }

    if (lgas) {
      content += `${lgas}`;
    }

    return content;
  }

  /**
   * Compose warning description
   * @param warningModel - The warning model
   */
  composeWarningDescription(warningModel: GetWarningsModel): string {
    return [warningModel['direction']?.toUpperCase(), warningModel['action-statement']?.toUpperCase(), warningModel['headline']]
      .filter(Boolean)

      .filter(Boolean)
      .join(' - ');
  }
}
